import { defineMessages, type IntlShape } from 'react-intl';

import type { CollaboratorAvatarKind } from '../presenceTypes.ts';

const i18n = defineMessages({
	rovo: {
		id: 'team-presence.rovo-agent.name',
		defaultMessage: 'Rovo',
		description: 'Display name for the Rovo agent in presence labels',
	},
	externalAgent: {
		id: 'team-presence.external-agent.name',
		defaultMessage: 'External agent',
		description: 'Display name for an external agent whose details could not be resolved',
	},
	nameWithActor: {
		id: 'team-presence.avatar-group.agent-with-actor-tooltip',
		defaultMessage: '{name} with {actorName}',
		description: 'Canvas facepile label for an agent acting on behalf of a user.',
	},
});

export type AgentLabelKind = Exclude<CollaboratorAvatarKind, 'user'>;

export type AgentPresenceKindOptions = {
	agentType?: string;
	isExternalAgentFallback?: boolean;
	isRovoAgentFallback?: boolean;
	kind?: CollaboratorAvatarKind;
};

export type AgentPresenceLabelOptions = AgentPresenceKindOptions & {
	actorName?: string;
	name?: string;
};

export type AgentPresenceMetadataOptions = Pick<AgentPresenceKindOptions, 'agentType' | 'kind'> & {
	agentId?: string;
};

const getFallbackLabelMessage = (kind?: AgentLabelKind) => {
	if (kind === 'rovo-agent') {
		return i18n.rovo;
	}

	if (kind === 'external-agent') {
		return i18n.externalAgent;
	}

	return undefined;
};

export const toAgentLabelKind = (
	kind?: CollaboratorAvatarKind,
): Exclude<CollaboratorAvatarKind, 'user'> | undefined => {
	if (kind === 'rovo-agent' || kind === 'external-agent') {
		return kind;
	}

	return undefined;
};

const toAgentLabelKindFromAgentType = (agentType?: string): AgentLabelKind | undefined => {
	if (!agentType || agentType === 'unknown') {
		return undefined;
	}

	if (agentType === 'rovo_chat' || agentType === 'convo-ai' || agentType === 'convo_ai') {
		return 'rovo-agent';
	}

	return 'external-agent';
};

export const getAgentPresenceKind = ({
	agentType,
	isExternalAgentFallback,
	isRovoAgentFallback,
	kind,
}: AgentPresenceKindOptions): AgentLabelKind | undefined => {
	const collaboratorKind = toAgentLabelKind(kind);

	if (collaboratorKind) {
		return collaboratorKind;
	}

	if (isRovoAgentFallback) {
		return 'rovo-agent';
	}

	const agentTypeKind = toAgentLabelKindFromAgentType(agentType);

	if (agentTypeKind) {
		return agentTypeKind;
	}

	if (isExternalAgentFallback) {
		return 'external-agent';
	}

	return undefined;
};

export const hasAgentPresenceMetadata = ({
	agentId,
	agentType,
	kind,
}: AgentPresenceMetadataOptions): boolean =>
	Boolean(agentId) || getAgentPresenceKind({ agentType, kind }) !== undefined;

export const getAgentPresenceDisplayName = (
	{ kind, name }: Pick<AgentPresenceLabelOptions, 'kind' | 'name'>,
	formatMessage: IntlShape['formatMessage'],
): string => {
	if (name) {
		return name;
	}

	const fallbackLabel = getFallbackLabelMessage(getAgentPresenceKind({ kind }));

	return fallbackLabel ? formatMessage(fallbackLabel) : '';
};

export const formatAgentPresenceLabel = (
	options: AgentPresenceLabelOptions,
	formatMessage: IntlShape['formatMessage'],
): string => {
	const kind = getAgentPresenceKind(options);
	const name = getAgentPresenceDisplayName({ kind, name: options.name }, formatMessage);

	if (!options.actorName) {
		return name;
	}

	return formatMessage(i18n.nameWithActor, {
		actorName: options.actorName,
		name,
	});
};
